import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/Button';
import { FaUserCircle, FaChevronDown, FaCog } from 'react-icons/fa';
import './Login.css';

interface LoginProps {
  onLogin?: () => void;
  /** 'sidebar' renders Settings/Sign out directly; 'compact' (default) uses a dropdown */
  variant?: 'sidebar' | 'compact';
}

const Login: React.FC<LoginProps> = ({ onLogin, variant = 'compact' }) => {
  const navigate = useNavigate();
  const { user, handleGoogleLogin, handleDevLogin } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        triggerRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle Escape key to close menu
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && isDropdownOpen) {
      setIsDropdownOpen(false);
      triggerRef.current?.focus();
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const closeMenu = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);

  const handleLogin = async () => {
    closeMenu();
    await handleGoogleLogin();
    onLogin?.();
  };

  // Helper to check if dev login should be enabled (dev mode OR REACT_APP_ENABLE_DEV_LOGIN)
  const isDevLoginEnabled = process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true';

  /* istanbul ignore next */
  const handleDevelopmentLogin = async () => {
    if (isDevLoginEnabled) {
      closeMenu();
      await handleDevLogin();
      onLogin?.();
    }
  };

  const handleSignOut = async () => {
    closeMenu();
    await handleGoogleLogin();
  };

  const handleSettings = () => {
    closeMenu();
    navigate('/settings');
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.log('Image failed to load:', e.currentTarget.src);
    e.currentTarget.src = 'https://via.placeholder.com/150';
  };

  if (user) {
    // Sidebar variant: show Settings and Sign out directly without a dropdown
    if (variant === 'sidebar') {
      return (
        <div className="sidebar-user-section">
          <p className="sidebar-user-name" title={user.displayName}>
            {user.displayName}
          </p>
          <div className="sidebar-user-actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSettings}
              fullWidth
              className="sidebar-nav-button sidebar-auth-button"
              leftIcon={FaCog({ "aria-hidden": "true" })}
            >
              Settings
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              fullWidth
              className="sidebar-nav-button sidebar-auth-button sidebar-signout-button"
            >
              Sign out
            </Button>
          </div>
        </div>
      );
    }

    // Compact variant: dropdown menu (used on mobile)
    return (
      <div className="user-profile" ref={dropdownRef}>
        <button
          ref={triggerRef}
          className={`profile-trigger ${isDropdownOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
          aria-label={`User menu for ${user.displayName}`}
          title={user.displayName}
        >
          {user.photo ? (
            <img
              src={user.photo}
              alt={`${user.displayName}'s avatar`}
              className="user-avatar"
              onError={handleImageError}
            />
          ) : (
            <span className="user-avatar-fallback">
              {FaUserCircle({ "aria-hidden": "true" })}
            </span>
          )}
          <span className={`profile-chevron ${isDropdownOpen ? 'open' : ''}`}>
            {FaChevronDown({ "aria-hidden": "true" })}
          </span>
        </button>
        {isDropdownOpen && (
          <>
            <div
              className="dropdown-backdrop"
              onClick={closeMenu}
              aria-label="Close menu"
              role="button"
            />
            <div
              className="dropdown-menu"
              role="menu"
              aria-label="User menu"
            >
              <div className="dropdown-header">
                <span className="dropdown-user-name">{user.displayName}</span>
                {user.email && (
                  <span className="dropdown-user-email">{user.email}</span>
                )}
              </div>
              <div className="dropdown-divider" />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSettings}
                role="menuitem"
                fullWidth
                leftIcon={FaCog({ "aria-hidden": "true" })}
              >
                Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                role="menuitem"
                fullWidth
              >
                Sign out
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Sidebar variant: show sign-in button(s) directly
  if (variant === 'sidebar') {
    return (
      <div className="sidebar-user-section">
        <div className="sidebar-user-actions">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogin}
            fullWidth
            className="sidebar-nav-button"
          >
            Sign in with Google
          </Button>
          {/* istanbul ignore next */}
          {isDevLoginEnabled && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDevelopmentLogin}
              fullWidth
              className="sidebar-nav-button"
            >
              Dev Login
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Compact variant: dropdown menu (used on mobile)
  return (
    <div className="login-container" ref={dropdownRef}>
      <div className="login-button-container">
        <Button
          ref={triggerRef}
          variant="primary"
          size="sm"
          onClick={toggleMenu}
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
          aria-label="Sign in menu"
          className={`login-button ${isDropdownOpen ? 'active' : ''}`}
        >
          Sign in ▾
        </Button>
        {isDropdownOpen && (
          <>
            <div
              className="dropdown-backdrop"
              onClick={closeMenu}
              aria-label="Close menu"
              role="button"
            />
            <div
              className="dropdown-menu"
              role="menu"
              aria-label="Sign in options"
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogin}
                role="menuitem"
                fullWidth
              >
                Sign in with Google
              </Button>
              {/* istanbul ignore next */}
              {isDevLoginEnabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDevelopmentLogin}
                  role="menuitem"
                  fullWidth
                >
                  Dev Login
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
